import {
  AccessTokenApi,
  GenerateAccessTokenRequest,
  GenerateAccessTokenResponse,
  RemoveAccessTokenRequest,
  RemoveAccessTokensRequest,
  GetAccessTokensRequest,
  SafeAccessTokenResponse,
  RotateAccessTokenRequest,
  RotateAccessTokenResponse,
} from './api/AccessTokenApi';
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { handleFailedResponse } from '@agilelab/plugin-wb-platform-common';

export class AccessTokenClient implements AccessTokenApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('auth');
  }

  async generateAccessToken(
    request: GenerateAccessTokenRequest,
  ): Promise<GenerateAccessTokenResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/access-tokens`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async removeAccessToken(request: RemoveAccessTokenRequest): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/access-tokens/${encodeURIComponent(request.uuid)}`;
    const response = await this.fetchApi.fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-By': request.askedBy,
      },
    });

    await handleFailedResponse(response);
  }

  async removeAccessTokens(request: RemoveAccessTokensRequest): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const url = new URL(`${baseUrl}/access-tokens`);
    url.searchParams.append('serviceAccountRef', request.serviceAccountRef);

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-By': request.askedBy,
      },
    });

    await handleFailedResponse(response);
  }

  async getAccessTokens(
    request: GetAccessTokensRequest,
  ): Promise<SafeAccessTokenResponse[]> {
    const baseUrl = await this.getBaseUrl();
    const url = new URL(
      `${baseUrl}/access-tokens/${encodeURIComponent(
        request.serviceAccountRef,
      )}`,
    );

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    await handleFailedResponse(response);
    return response.json();
  }

  async rotateAccessToken(
    request: RotateAccessTokenRequest,
  ): Promise<RotateAccessTokenResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/access-tokens/${encodeURIComponent(request.uuid)}`;
    const response = await this.fetchApi.fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updatedBy: request.updatedBy }),
    });

    await handleFailedResponse(response);
    return response.json();
  }
}
